// app/javascript/controllers/searchable_select_controller.js
// Enhances a hidden city field with a custom combobox that has an inline
// search box, so users can find cities that aren't visible in the short list.
// On selection it writes the value into the hidden field and dispatches a
// native "change" event, so the existing `change->search#submit` action fires
// exactly as it did with the old native <select>.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "button", "label", "panel", "search", "list", "empty"]
  static values = {
    allLabel: { type: String, default: "" },
    // When present, options are fetched from this endpoint instead of being
    // rendered inline. See app/views/shared/_city_select.html.erb for why: the
    // inline list was 1,292 buttons and 56% of the homepage's DOM nodes.
    url: { type: String, default: "" }
  }

  connect() {
    this.onDocumentClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
  }

  toggle(event) {
    event.preventDefault()
    if (this.panelTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      if (this.remote) {
        this.fetchOptions("")
      } else {
        this.applyFilter("")
      }
      // Focus after the panel is shown so the cursor lands in the search box.
      requestAnimationFrame(() => this.searchTarget.focus())
    }
  }

  get remote() {
    return this.urlValue.length > 0
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  filter() {
    if (this.remote) {
      this.fetchOptions(this.searchTarget.value)
    } else {
      this.applyFilter(this.searchTarget.value)
    }
  }

  // Debounced so a typed word is one request, not one per keystroke. The
  // in-flight request is aborted rather than left to resolve out of order: the
  // responses for "ber" and "berl" can arrive in either order, and the loser
  // would overwrite the list with stale options.
  fetchOptions(query) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      if (this.abortController) this.abortController.abort()
      this.abortController = new AbortController()

      const url = new URL(this.urlValue, window.location.origin)
      if (query.trim()) url.searchParams.set("q", query.trim())

      fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })
        .then((response) => (response.ok ? response.json() : { cities: [] }))
        .then((data) => this.renderOptions(data.cities || []))
        .catch((error) => {
          // An abort is the expected outcome of typing quickly, not a failure.
          if (error.name !== "AbortError") this.renderOptions([])
        })
    }, 180)
  }

  renderOptions(cities) {
    // The "all cities" reset option is rendered server-side and must survive a
    // refresh, so only the option rows after it are replaced.
    const reset = this.listTarget.querySelector('[data-value=""]')
    const resetRow = reset ? reset.closest("li") : null
    this.listTarget.innerHTML = ""
    if (resetRow) this.listTarget.appendChild(resetRow)

    cities.forEach((city) => {
      const li = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.action = "searchable-select#select"
      button.dataset.value = city
      button.dataset.label = city
      button.className = "block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50"
      // textContent, never innerHTML: these strings come from the database.
      button.textContent = city
      li.appendChild(button)
      this.listTarget.appendChild(li)
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", cities.length > 0)
    }
  }

  applyFilter(query) {
    const term = query.trim().toLowerCase()
    let visible = 0
    this.optionElements().forEach((el) => {
      const text = (el.dataset.label || el.textContent || "").toLowerCase()
      // Always keep the "all cities" reset option visible.
      const match = el.dataset.value === "" || text.includes(term)
      el.closest("li").classList.toggle("hidden", !match)
      if (match) visible += 1
    })
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }
  }

  select(event) {
    event.preventDefault()
    const el = event.currentTarget
    const value = el.dataset.value || ""
    const label = el.dataset.label || el.textContent.trim()

    this.fieldTarget.value = value
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = value === "" ? this.allLabelValue : label
    }
    this.close()
    // Re-use the existing search submit pipeline.
    this.fieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.buttonTarget.focus()
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      // Skip the "all cities" reset row. It is always present and always
      // visible, so it was what Enter selected every time: typing "Ham" and
      // pressing Enter cleared the filter instead of choosing Hamburg. Only
      // fall back to it when the search box is empty and it is the sole option.
      const options = this.optionElements().filter(
        (el) => !el.closest("li").classList.contains("hidden")
      )
      const typed = this.hasSearchTarget && this.searchTarget.value.trim().length > 0
      const match = typed ? options.find((el) => el.dataset.value !== "") : options[0]
      if (match) match.click()
    }
  }

  optionElements() {
    return Array.from(this.listTarget.querySelectorAll("[data-value]"))
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }
}
